import React from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  Phone, 
  MapPin, 
  Clock, 
  Car, 
  Calendar, 
  Edit2
} from 'lucide-react';

const ViewContactModal = ({
  isOpen,
  onClose,
  contact,
  onEdit
}) => {
  if (!isOpen || !contact) return null;

  const displayRole = contact.label || contact.role || 'Contact';
  const isWholeTrip = contact.assignment_type === 'whole_trip' || contact.date === 'Whole Trip' || (!contact.day_number && !contact.event_id);
  const isWholeDay = contact.assignment_type === 'whole_day';
  const assignmentText = isWholeTrip
    ? 'Whole Trip'
    : (isWholeDay
      ? `Specific Day — Day ${contact.day_number || ''}`
      : (contact.event_title ? `Day ${contact.day_number} · ${contact.event_title}` : `Day ${contact.day_number} · Event Assignment`));

  const pickupName = contact.pickup_location?.name || contact.pickup_location?.address || (typeof contact.pickup_location === 'string' ? contact.pickup_location : null);
  const dropName = contact.drop_location?.name || contact.drop_location?.address || (typeof contact.drop_location === 'string' ? contact.drop_location : null);
  const meetingName = contact.meeting_point?.name || contact.meeting_point?.address || (typeof contact.meeting_point === 'string' ? contact.meeting_point : null);

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 md:p-6 bg-slate-900/50 backdrop-blur-xs animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-slate-200 overflow-hidden flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] my-auto transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-white shrink-0">
          <div className="min-w-0 pr-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider shrink-0">
                {isWholeTrip ? 'Whole Trip' : `Day ${contact.day_number}`}
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#067978]/10 text-[#067978] shrink-0">
                {displayRole}
              </span>
            </div>
            <h3 className="text-base sm:text-lg font-bold text-slate-800 tracking-tight mt-0.5 break-words">
              {contact.name}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 overflow-y-auto space-y-3.5 text-xs text-slate-700 flex-1">
          {/* Phone & Assignment */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {contact.number && (
              <div className="p-3 bg-slate-50/80 rounded-xl border border-slate-100">
                <span className="text-[10px] uppercase font-semibold text-slate-400 block mb-1">
                  Phone Number
                </span>
                <div className="flex items-center gap-2 font-mono font-medium text-slate-800 text-xs">
                  <Phone size={13} className="text-[#067978]" />
                  <span>{contact.number}</span>
                </div>
              </div>
            )}

            <div className="p-3 bg-slate-50/80 rounded-xl border border-slate-100">
              <span className="text-[10px] uppercase font-semibold text-slate-400 block mb-1">
                Assignment
              </span>
              <div className="flex items-center gap-2 font-medium text-slate-800 text-xs">
                <Calendar size={13} className="text-[#067978]" />
                <span className="truncate">{assignmentText}</span>
              </div>
            </div>
          </div>

          {/* Vehicle Details (If Driver) */}
          {(contact.vehicle_type || contact.vehicle_number) && (
            <div className="p-3 bg-slate-50/80 rounded-xl border border-slate-100">
              <span className="text-[10px] uppercase font-semibold text-slate-400 block mb-1">
                Vehicle Details
              </span>
              <div className="flex items-center gap-2 text-slate-800">
                <Car size={13} className="text-[#067978]" />
                <span className="font-medium">{contact.vehicle_type || 'Vehicle'}</span>
                {contact.vehicle_number && (
                  <span className="font-mono text-slate-500 font-normal">({contact.vehicle_number})</span>
                )}
              </div>
            </div>
          )}

          {/* Pickup & Drop Points (If Driver) */}
          {(pickupName || dropName) && (
            <div className="p-3 bg-slate-50/80 rounded-xl border border-slate-100 space-y-2.5">
              <span className="text-[10px] uppercase font-semibold text-slate-400 block">
                Pickup &amp; Drop Route
              </span>
              {pickupName && (
                <div className="flex items-start gap-2">
                  <MapPin size={13} className="text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="text-[11px] font-medium text-slate-700 block">
                      Pickup: {pickupName}
                    </span>
                    {contact.pickup_time && (
                      <span className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5 font-mono">
                        <Clock size={10} /> {contact.pickup_time}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {dropName && (
                <div className="flex items-start gap-2 pt-1 border-t border-slate-200/60">
                  <MapPin size={13} className="text-rose-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="text-[11px] font-medium text-slate-700 block">
                      Drop: {dropName}
                    </span>
                    {contact.drop_time && (
                      <span className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5 font-mono">
                        <Clock size={10} /> {contact.drop_time}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Meeting Point (If Guide) */}
          {meetingName && (
            <div className="p-3 bg-slate-50/80 rounded-xl border border-slate-100">
              <span className="text-[10px] uppercase font-semibold text-slate-400 block mb-1">
                Meeting Point
              </span>
              <div className="flex items-start gap-2 text-slate-800">
                <MapPin size={13} className="text-[#067978] shrink-0 mt-0.5" />
                <span className="font-medium">{meetingName}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200/70 rounded-lg transition-all cursor-pointer"
          >
            Close
          </button>
          {onEdit && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onEdit(contact);
              }}
              className="px-3.5 py-1.5 bg-[#067978] hover:bg-[#056564] text-white text-xs font-medium rounded-lg shadow-2xs transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <Edit2 size={12} />
              <span>{displayRole === 'Driver' ? 'Edit Driver' : 'Edit Contact'}</span>
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ViewContactModal;
